/**
 * Recovery for failed lazy-route downloads ("Loading chunk N failed").
 *
 * The production client reaches the origin over a path that drops roughly a
 * third of its packets, so a single failed `import()` used to replace the whole
 * screen with an error page — even though the chunk was sitting on the server
 * intact. A retry fixes the dropped packet; the case that needs more than a
 * retry is a *cached* body: the service worker serves content-hashed assets
 * cache-first and nothing ever revalidates them, so one truncated download
 * stays broken until the cache is dropped.
 */

export const CHUNK_RELOAD_STORAGE_KEY = 'buzzkill:chunk-recovery-at';

// A reload that lands on the same broken build must not start a reload loop.
const RELOAD_GUARD_MS = 60000;

// The worker answers a CLEAR_CACHES message with CACHES_CLEARED.
const CACHE_CLEAR_TIMEOUT_MS = 2000;

export function isChunkLoadError(error) {
  const name = error?.name || '';
  const message = String(error?.message || error || '');
  return name === 'ChunkLoadError'
    || /Loading (?:CSS )?chunk \S+ failed/i.test(message)
    || /failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
}

/**
 * Run a route loader, retrying the transient failures this link produces.
 * Webpack discards a rejected chunk promise, so calling the loader again really
 * does re-request the file instead of replaying the cached error.
 */
export async function importRouteWithRetry(loader, delays = [0, 500, 1500]) {
  let lastError;
  for (let attempt = 0; attempt < delays.length; attempt += 1) {
    if (delays[attempt]) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, delays[attempt]); });
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      return await loader();
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error)) throw error;
    }
  }
  throw lastError;
}

/**
 * Drop the service-worker caches, then reload. The cached app shell names the
 * bundle hashes of the deploy it was stored from, and the static cache may hold
 * a truncated body under a content-hashed URL; either one makes a plain reload
 * fail in exactly the same way.
 *
 * Returns true when a reload was scheduled, false when one already happened
 * recently (the caller then shows an explanation instead of reloading forever).
 */
export function recoverFromChunkError({
  navigator: nav = typeof navigator !== 'undefined' ? navigator : undefined,
  location = typeof window !== 'undefined' ? window.location : undefined,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : undefined,
  now = Date.now,
  clearTimeoutMs = CACHE_CLEAR_TIMEOUT_MS
} = {}) {
  const startedAt = now();
  try {
    const previous = Number(storage?.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
    if (previous && startedAt - previous < RELOAD_GUARD_MS) return false;
    storage?.setItem(CHUNK_RELOAD_STORAGE_KEY, String(startedAt));
  } catch (_) { /* private mode: still reload, just without the loop guard */ }

  if (!location) return false;

  let done = false;
  const reload = () => {
    if (done) return;
    done = true;
    location.reload();
  };

  const controller = nav?.serviceWorker?.controller;
  if (!controller) {
    reload();
    return true;
  }

  const onMessage = (event) => {
    if (event?.data?.type === 'CACHES_CLEARED') reload();
  };
  nav.serviceWorker.addEventListener('message', onMessage);
  try {
    controller.postMessage({ type: 'CLEAR_CACHES' });
  } catch (_) { /* fall through to the timeout below */ }
  setTimeout(() => {
    nav.serviceWorker.removeEventListener('message', onMessage);
    reload();
  }, clearTimeoutMs);
  return true;
}
